import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import {
  ControlContainer,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ErrorStateOnTouched } from '@app/material/ErrorStateOnTouched';
import { NotificationService } from '@core/services/notification.service';
import { DamageReason, DeductibleInfo } from '@sales/modules/mobilephone/models/equipment-api.model';
import { EquipmentService } from '@sales/services/equipment.service';
import { IconNames } from '@shared/enums/icon-names';
import { Subscription } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatCheckbox } from '@angular/material/checkbox';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';
import { IceCardComponent } from '@app/shared/components/atoms/ice-card/ice-card.component';

enum Messages {
  DamageReasonsFetchError = 'Feil ved henting av skadeårsaker',
}

@Component({
  selector: 'app-mobile-phone-insurance',
  templateUrl: './mobile-phone-insurance.component.html',
  styleUrls: ['./mobile-phone-insurance.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [
    IceCardComponent,
    IceAlertComponent,
    SpinnerComponent,
    FormsModule,
    ReactiveFormsModule,
    MatCheckbox,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatSelect,
    MatOption,
    IceButtonComponent,
  ],
})
export class MobilePhoneInsuranceComponent implements OnInit, OnDestroy {
  private formGroupDirective = inject(FormGroupDirective);
  private formBuilder = inject(FormBuilder);
  private equipmentService = inject(EquipmentService);
  private notificationService = inject(NotificationService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  IconNames = IconNames;

  @Input() deductibleInfo: DeductibleInfo;
  @Input() isCreatingClaim: boolean;
  @Output() navigateToPreviousStep = new EventEmitter();
  @Output() useInsurance = new EventEmitter();

  form: FormGroup;
  damageReasons: DamageReason[];
  isLoadingDamageReasons: boolean;
  matcher = new ErrorStateOnTouched();
  subscriptions$: Subscription[] = [];
  today: Date;
  minDate: Date;

  ngOnInit() {
    this.today = new Date();
    this.minDate = new Date();
    this.minDate.setDate(this.minDate.getDate() - 365);
    if (!this.formGroupDirective.form.controls.insuranceGroup) {
      this.formGroupDirective.form.addControl(
        'insuranceGroup',
        this.formBuilder.group({
          deductibleInfoControl: this.formBuilder.control(false, Validators.requiredTrue),
          damageDateControl: this.formBuilder.control(null, Validators.required),
          damageReasonsTextControl: this.formBuilder.control('', [Validators.required, Validators.maxLength(511)]),
          damageReasonsControl: this.formBuilder.control(null, Validators.required),
        })
      );
    }
    this.form = this.formGroupDirective.form.controls.insuranceGroup as FormGroup;

    this.isLoadingDamageReasons = true;
    this.subscriptions$.push(
      this.equipmentService
        .getDamageReasons()
        .pipe(
          tap(reasons => {
            this.damageReasons = reasons;
            this.isLoadingDamageReasons = false;

            this.changeDetectorRef.detectChanges();
          }),
          catchError(err => {
            this.isLoadingDamageReasons = false;
            this.notificationService.error(Messages.DamageReasonsFetchError);
            throw err;
          })
        )
        .subscribe()
    );
  }

  get deductibleInfoControl(): FormControl {
    return this.form.controls.deductibleInfoControl as FormControl;
  }

  get damageDateControl(): FormControl {
    return this.form.controls.damageDateControl as FormControl;
  }

  get damageReasonsTextControl(): FormControl {
    return this.form.controls.damageReasonsTextControl as FormControl;
  }

  get damageReasonsControl(): FormControl {
    return this.form.controls.damageReasonsControl as FormControl;
  }

  onComplete() {
    this.useInsurance.emit({
      damageDate: this.form.controls.damageDateControl.value,
      damageReasonText: this.form.controls.damageReasonsTextControl.value,
      damageReason: this.form.controls.damageReasonsControl.value,
    });
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
